import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AuthShell } from "@/components/auth/AuthShell";
import { AuthHeroIcon } from "@/components/auth/AuthHeroIcon";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, LogIn, ArrowRight, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ClaimTokenPage = () => {
  const { token } = useParams<{ token: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"idle" | "processing" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [shelterName, setShelterName] = useState<string>("");

  const consume = async () => {
    if (!token) return;
    setStatus("processing");
    try {
      const { data, error } = await supabase.functions.invoke("consume-claim-token", {
        body: { token },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setShelterName(data?.shelter?.name ?? "");
      setStatus("success");
      toast.success("Uzyskano dostęp do profilu");
    } catch (err: any) {
      const msg = err?.context?.error || err?.message || "Nie udało się uzyskać dostępu do profilu";
      setErrorMsg(msg);
      setStatus("error");
    }
  };

  // Track click as soon as the page loads (before login), so admin sees how many
  // people opened the magic link even if they never finish registering.
  useEffect(() => {
    if (!token) return;
    supabase.rpc("track_claim_token_click" as never, { _token: token } as never).then(
      ({ error }) => {
        if (error) console.warn("[ClaimToken] track click failed", error);
      },
    );
  }, [token]);

  useEffect(() => {
    if (!loading && user && status === "idle") {
      consume();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Helmet>
          <title>Uzyskaj dostęp do profilu | GdzieSchronisko</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <AuthShell title="Już prawie na pokładzie">
          <div className="flex flex-col items-center text-center gap-5 py-2">
            <AuthHeroIcon icon={KeyRound} tone="primary" />
            <div className="space-y-1.5">
              <p className="text-base font-semibold">Zaloguj się, aby przejąć profil</p>
              <p className="text-sm text-muted-foreground max-w-sm">
                Aby powiązać schronisko z Twoim kontem, najpierw zaloguj się lub załóż nowe.
              </p>
            </div>
            <div className="w-full space-y-2.5 pt-1">
              <Button asChild size="lg" className="w-full gap-2">
                <Link to={`/logowanie?next=/przejmij/${token}`}>
                  <LogIn className="h-4 w-4" /> Zaloguj się
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full">
                <Link to={`/rejestracja?next=/przejmij/${token}`}>Załóż konto</Link>
              </Button>
            </div>
          </div>
        </AuthShell>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Uzyskaj dostęp do profilu | GdzieSchronisko</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <AuthShell title={status === "success" ? "Witamy w zespole!" : status === "error" ? "Coś poszło nie tak" : "Łączymy Cię ze schroniskiem"}>
        {status === "processing" && (
          <div className="flex flex-col items-center text-center gap-5 py-4">
            <div className="relative">
              <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 blur-xl" />
              <div className="relative h-20 w-20 rounded-full bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                <Loader2 className="h-9 w-9 animate-spin text-primary" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-base font-semibold">Weryfikujemy token</p>
              <p className="text-sm text-muted-foreground max-w-sm">
                Sprawdzamy uprawnienia i przygotowujemy Twój panel - to potrwa moment.
              </p>
            </div>
          </div>
        )}
        {status === "success" && (
          <div className="flex flex-col items-center text-center gap-5 py-4">
            <AuthHeroIcon icon={CheckCircle2} tone="primary" sparkle />
            <div className="space-y-1.5">
              <p className="text-xl font-bold">Wszystko gotowe</p>
              <p className="text-sm text-muted-foreground max-w-sm">
                {shelterName ? (
                  <>Od teraz zarządzasz profilem <strong className="text-foreground">{shelterName}</strong>. Możesz aktualizować potrzeby, dane kontaktowe i zapraszać zespół.</>
                ) : (
                  "Od teraz masz pełny dostęp do panelu schroniska."
                )}
              </p>
            </div>
            <Button onClick={() => navigate("/panel")} size="lg" className="gap-2 mt-2">
              Przejdź do panelu <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        {status === "error" && (
          <div className="flex flex-col items-center text-center gap-5 py-4">
            <AuthHeroIcon icon={XCircle} tone="destructive" />
            <div className="space-y-1.5">
              <p className="text-base font-semibold">Nie udało się przejąć profilu</p>
              <p className="text-sm text-muted-foreground max-w-sm">{errorMsg}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <Button asChild variant="outline" size="lg" className="flex-1">
                <Link to="/">Strona główna</Link>
              </Button>
              <Button asChild size="lg" className="flex-1 gap-2">
                <Link to="/panel"><ShieldCheck className="h-4 w-4" /> Mój panel</Link>
              </Button>
            </div>
          </div>
        )}
      </AuthShell>
    </>
  );
};

export default ClaimTokenPage;
